/**
 * Token-gate for the /overview investor page.
 *
 * Wraps the real InvestorOverviewPage so the URL itself doesn't expose its
 * existence. Validation is server-side via /api/investor/verify-overview-key
 * — the frontend only checks the response and never holds the truth.
 *
 * Auth-failure UI: a generic 404. No "wrong password" hint, no "investor
 * area" branding. The URL just looks dead unless you have the right key.
 *
 * Usage:
 *   /overview?key=<token-from-env>      → renders investor page
 *   /overview                          → renders 404
 *   /overview?key=wrong                → renders 404
 *
 * The token is read from /api/investor/key-active so we can revoke without
 * a frontend redeploy if a link ever leaks.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import InvestorOverviewPage from './InvestorOverviewPage';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestorOverviewGate() {
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [state, setState] = useState('checking'); // 'checking' | 'allow' | 'deny'

  useEffect(() => {
    if (!key) {
      setState('deny');
      return;
    }
    // Server-side validation — never trust the frontend with the truth.
    axios
      .get(`${API}/api/investor/verify-overview-key`, {
        params: { key },
        validateStatus: () => true,
      })
      .then(({ status, data }) => {
        setState(status === 200 && data?.valid ? 'allow' : 'deny');
      })
      .catch(() => setState('deny'));
  }, [key]);

  // ── Heartbeat — track attentive time on the gated page (iter 86.5).
  // POSTs to /api/investor/heartbeat every 5 seconds, but ONLY while the
  // tab is visible AND focused. Idle / hidden tabs do not accumulate.
  // The same heartbeat also drives our auto-revoke detector — if a key
  // is opened from too many distinct sessions, the backend hard-revokes
  // mid-poll, the next verify call returns valid=false, and the page
  // gracefully transitions to 404.
  useEffect(() => {
    if (state !== 'allow' || !key) return;
    let intervalId = null;
    let revokeWatchId = null;
    const HEARTBEAT_MS = 5000;
    const ping = async () => {
      if (document.hidden) return;
      try {
        await axios.post(`${API}/api/investor/heartbeat`,
          { seconds: HEARTBEAT_MS / 1000 },
          { params: { key }, validateStatus: () => true, withCredentials: true });
      } catch { /* network blip — try next tick */ }
    };
    const startPinging = () => {
      if (intervalId) return;
      ping(); // fire immediately on (re)gain focus
      intervalId = setInterval(ping, HEARTBEAT_MS);
    };
    const stopPinging = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    // Auto-revoke watcher — every 30s re-verify the key. If the backend
    // has auto-revoked between heartbeats, flip back to deny immediately.
    const reverify = async () => {
      try {
        const { status, data } = await axios.get(`${API}/api/investor/verify-overview-key`,
          { params: { key }, validateStatus: () => true });
        if (status !== 200 || !data?.valid) setState('deny');
      } catch { /* keep showing page on network blip */ }
    };
    revokeWatchId = setInterval(reverify, 30_000);

    const handleVis = () => { document.hidden ? stopPinging() : startPinging(); };
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('focus', startPinging);
    window.addEventListener('blur', stopPinging);
    if (!document.hidden) startPinging();

    return () => {
      stopPinging();
      if (revokeWatchId) clearInterval(revokeWatchId);
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('focus', startPinging);
      window.removeEventListener('blur', stopPinging);
    };
  }, [state, key]);

  if (state === 'checking') {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] flex items-center justify-center" data-testid="overview-gate-checking">
        <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
      </div>
    );
  }

  if (state === 'deny') {
    return <NotFoundPage />;
  }

  return <InvestorOverviewPage />;
}

/**
 * Generic 404 page — intentionally blank-feeling. No branding, no hint that
 * the URL ever did anything else. Keeps the investor area properly private.
 */
function NotFoundPage() {
  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="overview-gate-404">
      <div className="max-w-sm text-center space-y-3">
        <h1 className="text-7xl font-black tracking-tight text-gray-700">404</h1>
        <p className="text-sm text-gray-500">This page doesn&apos;t exist.</p>
        <Link
          to="/login"
          data-testid="overview-gate-home-link"
          className="inline-block mt-4 text-xs text-gray-600 hover:text-gray-300 underline-offset-2 hover:underline transition"
        >
          Return home
        </Link>
      </div>
    </div>
  );
}
